import React, { useRef, useState, useEffect } from 'react'
import { BulletSteps, FlashMessage, GlobalLoader, InputField, Modal, SelectInput } from '@reactiveonline/reactive_admin_shared-packages'
import { emailValidation } from '@reactiveonline/reactive_admin_shared-packages/utils'

const steps = [
  { key: 'organization_data', next: 'client_data', icon: 'fa-building' },
  { key: 'client_data',       next: 'overview',    icon: 'fa-address-card'},
  { key: 'overview',          next: null,          icon: 'fa-clipboard-list'}
]

const durationOptions = [
  { title: 1,  value: 1, appliesToNormalFlow: true },
  { title: 12, value: 12, appliesToPromotion: true, appliesToNormalFlow: true },
  { title: 18, value: 18, appliesToPromotion: true },
  { title: 30, value: 30, appliesToPromotion: true },
]

const paymentMethodOptions = [
  { title: 'Card', value: 'Card' },
  { title: 'IBAN', value: 'iban' },
  { title: 'Worldline eshop', value: 'worldline eshop', appliesToPromotion: true },
  { title: 'Worldline MBS', value: 'worldline mbs', appliesToPromotion: true },
]

const businessTypeOptions = [
  { title: Partners.translations.tenant.business_types.individual, value: 'individual' },
  { title: Partners.translations.tenant.business_types.other, value: 'other' }
]

const initialUserDataState = {
  email: '',
  firstName: '',
  lastName: '',
  companyName: '',
  storeName: '',
  merchantId: '',
  businessType: businessTypeOptions[0],
  country: { value: 'GR', title: 'Greece' },
  stateCity: '',
  postalCode: '',
  address: '',
  vatNumber: '',
  phone: '',
  mobilePhone: '',
  plan: { title: '', value: '' },
  duration: durationOptions[1],
  paymentMethod: paymentMethodOptions[1]
}

export default function CreateTenantModal({
  admin, availableOrganizationData, closeModal, createRegistrationsPath, countries, getAvailableFlavoursAndPackagesPath
}) {
  const [currentStep, setCurrentStep] = useState(steps[0])
  const [selectedOrganizationData, setSelectedOrganizationData] = useState({
    organization: availableOrganizationData.organizations[0],
    branch: availableOrganizationData.organizations[0]?.branches?.[0],
    user: availableOrganizationData.organizations[0]?.branches?.[0] ? availableOrganizationData.organizations[0]?.branches?.[0]?.users?.[0] : availableOrganizationData.organizations[0].users[0]
  })
  const [userDataState, setUserDataState] = useState(initialUserDataState)
  const [availablePackages, setAvailablePackages] = useState({})
  const [availableFlavours, setAvailableFlavours] = useState([])
  const [loading, setLoading] = useState(false)

  const didMountRef = useRef(false)
  const flashMessageRef = useRef(null)

  useEffect(() => {
    setSelectedOrganizationData(prevState => ({ 
      ...prevState, 
      branch: selectedOrganizationData?.organization?.branches?.[0],
      user: selectedOrganizationData?.organization?.branches?.[0] ? selectedOrganizationData?.organization?.branches?.[0]?.users?.[0] : selectedOrganizationData?.organization?.users?.[0]
    }))
  }, [selectedOrganizationData.organization?.value])

  useEffect(() => {
    setSelectedOrganizationData(prevState => ({ 
      ...prevState, 
      user: selectedOrganizationData?.branch?.users?.[0] ?? selectedOrganizationData?.organization?.users?.[0]
    }))
  }, [selectedOrganizationData.branch?.value])

  useEffect(() => {
    if (didMountRef.current) {
      setUserDataState(prevState => ({
        ...prevState,
        plan: availablePackages[prevState.flavour.value][0]
      }))
    } else {
      didMountRef.current = true
    }
  }, [userDataState.flavour?.value])

  useEffect(() => {
    setLoading(true)

    Rails.ajax({
      type: 'GET',
      url: getAvailableFlavoursAndPackagesPath,
      dataType: 'json',
      success: res => {
        setLoading(false)

        flashMessageRef.current.show(res.error, 'error')
        
        const flavours = res.items.map(el => ({
          title: Object.values(el)[0].title,
          value: Object.keys(el)[0]
        }))

        const plans = res.items.reduce((acc, curr) => {
          acc[Object.keys(curr)[0]] = Object.values(curr)[0].plans

          return acc
        }, {})

        setAvailableFlavours(flavours)
        setAvailablePackages(plans)
        setUserDataState(prevState => ({ 
          ...prevState, 
          flavour: flavours[0],
          plan: plans[flavours[0]?.value]?.[0]
        }))
      }
    })
  }, [])

  const changeDuration = option => setUserDataState(prevState => ({ ...prevState, duration: option }))

  const changePaymentMethod = option => { 
    setUserDataState(prevState => ({ 
      ...prevState,
      duration: prevState.duration.appliesToPromotion === option.appliesToPromotion ? prevState.duration : durationOptions.find(el => el.appliesToPromotion === option.appliesToPromotion),
      paymentMethod: option 
    }))
  }

  const checkIfCanProceedToNextStep = () => {
    if (currentStep.key === 'organization_data') {
      return true
    }

    const { email, companyName, vatNumber } = userDataState

    let canProceed = true

    if (!emailValidation(email)) {
      canProceed = false
      flashMessageRef.current.show(Partners.translations.tenant.invalid_email, 'error')
    }

    if (!companyName) {
      canProceed = false
      flashMessageRef.current.show(Partners.translations.tenant.company_name_required, 'error')
    }

    if (!vatNumber) {
      canProceed = false
      flashMessageRef.current.show(Partners.translations.tenant.vat_required, 'error')
    }

    return canProceed
  }

  const proceedToNextStep = _e => {
    const nextStep = steps.find(step => step.key === currentStep.next)

    if (currentStep.key === 'overview') {
      createRegistration()

      return
    }
     
    if (checkIfCanProceedToNextStep()) {
      setCurrentStep(nextStep)
    }
  }

  const handleBackClick = _e => {
    const previousStep = steps.find(step => step.next === currentStep.key)

    if (previousStep) {
      setCurrentStep(previousStep)
    }
  }

  const renderSteps = () => {
    const currentStepIndex = steps.findIndex(step => step.key === currentStep.key)

    return (
      <BulletSteps
        stepsCount={ 3 }
        currentStep={ currentStepIndex }
        setCurrentStep={ index => index < currentStepIndex ? setCurrentStep(steps[index]) : null }
      />
    )
  }

  const checkIfActionShouldBeDisabled = () => {
    return availableFlavours.length === 0
  }

  const calculateRelevantActionText = () => {
    if (currentStep.key === 'overview') {
      return ReactiveCore.translations.create
    } else {
      return ReactiveCore.translations.next
    }
  }

  const renderOverviewRow = (title, value, extraClasses) => {
    return (
      <div
        className={ `flex-box content-space-between ${ extraClasses ?? '' }` }
      >
        <div className='bold'>{ title }:</div>
        <div style={{ flexBasis: '60%', textAlign: 'right', flexGrow: 0 }}>
          { value }
        </div>
      </div>
    )
  }

  const createRegistration = () => {
    const fd = new FormData()

    fd.append('registration[organization_id]', selectedOrganizationData.organization.value)
    if (selectedOrganizationData.branch && selectedOrganizationData.branch.value > 0) {
      fd.append('registration[branch_id]', selectedOrganizationData.branch.value)
    }
    fd.append('registration[administrator_id]', selectedOrganizationData.user.value)
    fd.append('registration[email]', userDataState.email)

    if (userDataState.firstName) {
      fd.append('registration[first_name]', userDataState.firstName)
    }

    if (userDataState.lastName) {
      fd.append('registration[last_name]', userDataState.lastName)
    }
    
    if (userDataState.companyName) {
      fd.append('registration[company]', userDataState.companyName)
    }

    fd.append('registration[country]', userDataState.country.value)

    if (userDataState.stateCity) {
      fd.append('registration[city]', userDataState.stateCity)
    }

    if (userDataState.postalCode) {
      fd.append('registration[postal_code]', userDataState.postalCode)
    }

    if (userDataState.address) {
      fd.append('registration[street]', userDataState.address)
    }

    if (userDataState.vatNumber) {
      fd.append('registration[vat_number]', userDataState.vatNumber)
    }

    if (userDataState.phone) {
      fd.append('registration[phone]', userDataState.phone)
    }

    if (userDataState.mobilePhone) {
      fd.append('registration[mobile_phone]', userDataState.mobilePhone)
    }

    fd.append('registration[flavour]', userDataState.flavour.value)
    fd.append('registration[plan]', userDataState.plan.title)
    fd.append('registration[duration]', userDataState.duration.value)
    fd.append('registration[payment_type]', userDataState.paymentMethod.value)
    fd.append('registration[business_type]', userDataState.businessType.value)
    fd.append('registration[merchant_id]', userDataState.merchantId)
    fd.append('registration[store_name]', userDataState.storeName)

    setLoading(true)

    Rails.ajax({
      type: 'POST',
      url: createRegistrationsPath,
      dataType: 'json',
      data: fd,
      success: res => {
        if (res.redirectUrl) {
          window.location.href = res.redirectUrl
        }
      },
      error: res => {
        setLoading(false)

        flashMessageRef.current.show(res.error, 'error')
      }
    })
  }

  return (
    <>
      <Modal
        visible
        closeModal={ closeModal }
        mode='xlarge'
        cancelAction={ currentStep.key !== 'organization_data' ? handleBackClick : null }
        cancelActionText={ ReactiveCore.translations.back }
        cancelActionIcon='fa-thin fa-chevron-left'
        action={ proceedToNextStep }
        actionText={ calculateRelevantActionText() }
        footerMiddleActions={ renderSteps }
        disableAction={ checkIfActionShouldBeDisabled() }
        showConfirmation={ currentStep.key === 'overview' }
        confirmationText={ Partners.translations.tenant.confirmation_create_text }
      >
        <div className='flex-box flex-column content-center flex-1'>
          <div>
            <div className='flex-box items-center m-b-10 bold' style={{ textAlign: 'left', gap: 10 }}>
              <div>
                <i className={ `fa-light fa-2x ${ currentStep.icon }` } />
              </div>
              <div>
                { Partners.translations.registration[currentStep.key] }
              </div>
            </div>
            <div style={{ height: 2, backgroundColor: '#DDD', width: 100, marginTop: 10 }}></div>
          </div>

          { currentStep.key === 'organization_data' &&
            <>
              <div>
                <SelectInput
                  label={ Partners.translations.organization.model_name }
                  options={ availableOrganizationData.organizations }
                  currentOption={ selectedOrganizationData.organization?.value }
                  setCurrentOption={ option => setSelectedOrganizationData(prevState => ({ ...prevState, organization: option })) }
                />

                { selectedOrganizationData.branch &&
                  <SelectInput
                    label={ Partners.translations.branch.model_name }
                    options={ availableOrganizationData.organizations.find(org => org.value === selectedOrganizationData.organization.value )?.branches || [] }
                    currentOption={ selectedOrganizationData.branch?.value }
                    setCurrentOption={ option => setSelectedOrganizationData(prevState => ({ ...prevState, branch: option })) }
                  />
                }

                <SelectInput
                  label={ Partners.translations.user.singular }
                  options={ (selectedOrganizationData.branch && selectedOrganizationData.branch.users) ? selectedOrganizationData.branch.users : selectedOrganizationData.organization.users }
                  currentOption={ selectedOrganizationData.user?.value }
                  setCurrentOption={ option => setSelectedOrganizationData(prevState => ({ ...prevState, user: option })) }
                />
              </div>
            </>
          }

          { currentStep.key === 'client_data' &&
            <>
              <div className='flex-box flex-wrap'>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <SelectInput
                    label={ Partners.translations.tenant.flavour }
                    options={ availableFlavours }
                    currentOption={ userDataState.flavour.value }
                    setCurrentOption={ option => setUserDataState(prevState => ({ ...prevState, flavour: option })) }
                  />
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <SelectInput
                    label={ Partners.translations.tenant.plan }
                    options={ availablePackages[userDataState.flavour.value] }
                    currentOption={ userDataState.plan.value }
                    setCurrentOption={ option => setUserDataState(prevState => ({ ...prevState, plan: option })) }
                  />
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <SelectInput
                    label={ Partners.translations.tenant.payment_method }
                    options={ paymentMethodOptions }
                    currentOption={ userDataState.paymentMethod.value }
                    setCurrentOption={ changePaymentMethod }
                  />
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <SelectInput
                    label={ Partners.translations.tenant.duration }
                    options={ userDataState.paymentMethod.appliesToPromotion ? durationOptions.filter(el => el.appliesToPromotion) : durationOptions.filter(el => el.appliesToNormalFlow) }
                    currentOption={ userDataState.duration.value }
                    setCurrentOption={ changeDuration }
                  />
                </div>

                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ `${ Partners.translations.administrator.attributes.email } *` }
                  >
                    <input
                      type='text'
                      value={ userDataState.email }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, email: e.target.value })) }
                    />
                  </InputField>
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ `${ Partners.translations.tenant.company_name } *` }
                  >
                    <input
                      type='text'
                      value={ userDataState.companyName }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, companyName: e.target.value })) }
                    />
                  </InputField>
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ `${ Partners.translations.tenant.vat_number } *` }
                  >
                    <input
                      type='text'
                      value={ userDataState.vatNumber }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, vatNumber: e.target.value })) }
                    />
                  </InputField>
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ Partners.translations.tenant.first_name }
                  >
                    <input
                      type='text'
                      value={ userDataState.firstName }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, firstName: e.target.value })) }
                    />
                  </InputField>
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ Partners.translations.tenant.last_name }
                  >
                    <input
                      type='text'
                      value={ userDataState.lastName }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, lastName: e.target.value })) }
                    />
                  </InputField>
                </div>

                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ Partners.translations.tenant.store_name }
                  >
                    <input
                      type='text'
                      value={ userDataState.storeName }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, storeName: e.target.value })) }
                    />
                  </InputField>
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ `${ Partners.translations.tenant.merchant_id }` }
                  >
                    <input
                      type='text'
                      value={ userDataState.merchantId }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, merchantId: e.target.value })) }
                    />
                  </InputField>
                </div>

                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <SelectInput
                    label={ Partners.translations.tenant.business_type }
                    options={ businessTypeOptions }
                    currentOption={ userDataState.businessType.value }
                    setCurrentOption={ option => setUserDataState(prevState => ({ ...prevState, businessType: option })) }
                  />
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <SelectInput
                    label={ Partners.translations.tenant.country }
                    options={ countries }
                    currentOption={ userDataState.country.value }
                    setCurrentOption={ option => setUserDataState(prevState => ({ ...prevState, country: option })) }
                  />
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ Partners.translations.tenant.city_state }
                  >
                    <input
                      type='text'
                      value={ userDataState.stateCity }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, stateCity: e.target.value })) }
                    />
                  </InputField>
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ Partners.translations.tenant.postal_code }
                  >
                    <input
                      type='text'
                      value={ userDataState.postalCode }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, postalCode: e.target.value })) }
                    />
                  </InputField>
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ Partners.translations.tenant.address }
                  >
                    <input
                      type='text'
                      value={ userDataState.address }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, address: e.target.value })) }
                    />
                  </InputField>
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ Partners.translations.tenant.phone }
                  >
                    <input
                      type='text'
                      value={ userDataState.phone }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, phone: e.target.value })) }
                    />
                  </InputField>
                </div>
                <div className='p-r-5 p-l-5' style={{ flex: '0 0 33.3333%' }}>
                  <InputField
                    label={ Partners.translations.tenant.mobile_phone }
                  >
                    <input
                      type='text'
                      value={ userDataState.mobilePhone }
                      onChange={ e => setUserDataState(prevState => ({ ...prevState, mobilePhone: e.target.value })) }
                    />
                  </InputField>
                </div>
              </div>
            </>
          }

          { currentStep.key === 'overview' &&
            <>
              <div style={{ width: '100%', gap: 20 }} className='flex-box flex-wrap m-t-30'>
                <div className='card flex-1' style={{ border: '1px solid #dddddd' }}>
                  <div className='card-content'>
                    <div className='card-title' style={{ fontSize: '18px', borderBottom: '1px solid lightgray', paddingBottom: 24 }}>
                      { Partners.translations.registration.organization_data }
                    </div>
                    <div className='card-body' style={{ paddingTop: 24, paddingBottom: 24 }}>
                      { renderOverviewRow(Partners.translations.organization.model_name, selectedOrganizationData.organization.title) }
                      { !!selectedOrganizationData.branch && selectedOrganizationData.branch.title !== '-' &&
                        renderOverviewRow(Partners.translations.branch.model_name, selectedOrganizationData.branch.title)
                      }
                      { renderOverviewRow(Partners.translations.user.singular, selectedOrganizationData.user.title) }
                    </div>
                  </div>
                </div>

                <div className='card flex-1' style={{ border: '1px solid #dddddd' }}>
                  <div className='card-content'>
                    <div className='card-title' style={{ fontSize: '18px', borderBottom: '1px solid lightgray', paddingBottom: 24 }}>
                      { Partners.translations.registration.client_data }
                    </div>
                    <div className='card-body' style={{ paddingTop: 24, paddingBottom: 24 }}>
                      { renderOverviewRow(Partners.translations.administrator.attributes.email, userDataState.email) }
                      { renderOverviewRow(Partners.translations.tenant.first_name, userDataState.firstName || '-') }
                      { renderOverviewRow(Partners.translations.tenant.last_name, userDataState.lastName || '-') }
                      { renderOverviewRow(Partners.translations.tenant.company_name, userDataState.companyName || '-') }
                      { renderOverviewRow(Partners.translations.tenant.business_type, userDataState.businessType.title || '-') }
                      { renderOverviewRow(Partners.translations.tenant.country, userDataState.country.title) }
                      { renderOverviewRow(Partners.translations.tenant.city_state, userDataState.stateCity || '-') }
                      { renderOverviewRow(Partners.translations.tenant.postal_code, userDataState.postalCode || '-') }
                      { renderOverviewRow(Partners.translations.tenant.address, userDataState.address || '-') }
                      { renderOverviewRow(Partners.translations.tenant.vat_number, userDataState.vatNumber || '-') }
                      { renderOverviewRow(Partners.translations.tenant.phone, userDataState.phone || '-') }
                      { renderOverviewRow(Partners.translations.tenant.mobile_phone, userDataState.mobilePhone || '-') }
                      { renderOverviewRow(Partners.translations.tenant.payment_method, userDataState.paymentMethod.title) }
                      { renderOverviewRow(Partners.translations.tenant.flavour, userDataState.flavour.title) }
                      { renderOverviewRow(Partners.translations.tenant.plan, userDataState.plan.title) }
                      { renderOverviewRow(Partners.translations.tenant.duration, userDataState.duration.title) }
                    </div>
                  </div>
                </div>
              </div>
            </>
          }
        </div>

        { loading && <GlobalLoader fullPage /> }
      </Modal>

      <FlashMessage ref={ flashMessageRef } />
    </>
  )
}